import type { CanvasView } from "./viewport";

export type CameraScheduler = {
  frame: (callback: () => void) => number;
  cancelFrame: (id: number) => void;
  delay: (callback: () => void) => number;
  cancelDelay: (id: number) => void;
  now?: () => number;
  reducedMotion?: () => boolean;
};

const browserScheduler: CameraScheduler = {
  frame: (callback) => requestAnimationFrame(callback),
  cancelFrame: (id) => cancelAnimationFrame(id),
  delay: (callback) => window.setTimeout(callback, 180),
  cancelDelay: (id) => window.clearTimeout(id),
  now: () => performance.now(),
  reducedMotion: () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
};

function sameView(a: CanvasView, b: CanvasView) {
  return a.zoom === b.zoom && a.pan.x === b.pan.x && a.pan.y === b.pan.y;
}

/** Input stays current immediately; only the small camera subscribers paint per frame. */
export class CanvasCamera {
  readonly viewRef: { current: CanvasView };
  private snapshot: { view: CanvasView; moving: boolean };
  private listeners = new Set<() => void>();
  private frame: number | null = null;
  private delay: number | null = null;
  private dirty = false;
  private coasting = false;
  private samples: { x: number; y: number; time: number }[] = [];

  constructor(
    view: CanvasView,
    public onCommit?: (view: CanvasView) => void,
    private scheduler = browserScheduler,
  ) {
    this.viewRef = { current: view };
    this.snapshot = { view, moving: false };
  }

  getView = () => this.viewRef.current;
  getSnapshot = () => this.snapshot;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  private publish(moving: boolean) {
    if (sameView(this.snapshot.view, this.getView()) && this.snapshot.moving === moving) return;
    this.snapshot = { view: this.getView(), moving };
    this.listeners.forEach((listener) => listener());
  }

  setView = (view: CanvasView) => {
    this.stopMomentum();
    if (sameView(this.getView(), view)) return;
    this.viewRef.current = view;
    this.dirty = true;
    if (this.frame === null) this.frame = this.scheduler.frame(() => {
      this.frame = null;
      this.publish(true);
    });
    if (this.delay !== null) this.scheduler.cancelDelay(this.delay);
    this.delay = this.scheduler.delay(this.flush);
  };

  /** A fresh grab, wheel input or explicit camera command takes over immediately. */
  stopMomentum = () => {
    if (!this.coasting) return;
    this.coasting = false;
    if (this.frame !== null) this.scheduler.cancelFrame(this.frame);
    this.frame = null;
    this.publish(true);
    this.delay = this.scheduler.delay(this.flush);
  };

  beginPan = (time: number) => {
    this.stopMomentum();
    this.samples = [{ ...this.getView().pan, time }];
  };

  panTo = (view: CanvasView, time: number) => {
    this.setView(view);
    this.recordPan(time);
  };

  recordPan = (time: number) => {
    this.samples.push({ ...this.getView().pan, time });
    // A short history smooths event noise while following changes in direction.
    this.samples = this.samples.filter((sample) => time - sample.time <= 100);
  };

  endPan = (time: number, cancel = false) => {
    const samples = this.samples;
    this.samples = [];
    const first = samples[0], last = samples.at(-1);
    if (cancel || this.scheduler.reducedMotion?.() || !first || !last ||
      time - last.time > 80 || time - first.time <= 0) return;
    const elapsed = time - first.time;
    let vx = (last.x - first.x) / elapsed, vy = (last.y - first.y) / elapsed;
    const speed = Math.hypot(vx, vy);
    if (speed < 0.08) return;
    const limit = Math.min(1, 3 / speed);
    vx *= limit; vy *= limit;
    this.cancel();
    this.coasting = true;
    const now = this.scheduler.now || (() => performance.now());
    let previous = now();
    const tick = () => {
      this.frame = null;
      const current = now(), dt = Math.max(0, current - previous);
      previous = current;
      // Integrate exponential friction in screen pixels: identical feel at every
      // zoom and refresh rate, without adding latency beneath the pointer.
      const decay = Math.exp(-dt / 280), distance = 280 * (1 - decay);
      const view = this.getView();
      this.viewRef.current = { ...view, pan: {
        x: view.pan.x + vx * distance,
        y: view.pan.y + vy * distance,
      } };
      this.dirty = true;
      vx *= decay; vy *= decay;
      if (Math.hypot(vx, vy) < 0.02) {
        this.flush();
        return;
      }
      this.publish(true);
      this.frame = this.scheduler.frame(tick);
    };
    this.frame = this.scheduler.frame(tick);
  };

  private cancel() {
    this.coasting = false;
    if (this.frame !== null) this.scheduler.cancelFrame(this.frame);
    if (this.delay !== null) this.scheduler.cancelDelay(this.delay);
    this.frame = this.delay = null;
  }

  /** Also called before pagehide/unmount, when an animation frame may never run. */
  flush = () => {
    this.cancel();
    const dirty = this.dirty;
    this.dirty = false;
    this.publish(false);
    if (dirty) this.onCommit?.(this.getView());
  };

  /** An explicit restored/page/focus view supersedes any queued input. */
  restore(view: CanvasView) {
    if (sameView(this.getView(), view) && !this.coasting) return;
    this.cancel();
    this.dirty = false;
    this.viewRef.current = view;
    this.publish(false);
  }
}
